import i18next from "i18next";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { JetSmartEvent } from "Services/eventbus/JetSmartEvent";
import { PROMO_CODE_REGEX, VOUCHER_REGEX } from "Services/constants";
import { getPromoCodes } from "ComponentHelpers/PromoCodeHelper";
import { useState } from "haunted";
import { iconButton } from "Components/iconButton";
import { useEffectWithCleanup, useEffectWithoutCleanup } from "Shared/haunted/CustomHooks";
import { useReduxState } from "Shared/redux/useReduxState";
import { useWindowSize } from "Shared/haunted/useWindowSize";
import classNames from "classnames";
import { html } from "lit-html";
export interface Props {
	culture: string;
	currentSearchManager: CurrentSearchManager;
	dynamicSettings: DynamicSettings;
}

export const usePromoCodeBox = (props: Props) => {
	const windowSize = useWindowSize();

	const [userInfo] = useReduxState("userInfo");

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isInitialized, setIsInitialized] = useState<boolean>(false);

	const [promoCode, setPromoCode] = useState<string>("");
	const [showPromoCodeFormatError, setShowPromoCodeFormatError] = useState<boolean>(false);
	const [showPromoCodeVoucherError, setShowPromoCodeVoucherError] = useState<boolean>(false);
	const [predefinedPromoCodes, setPredefinedPromoCodes] = useState<string[]>([]);

	const currentPromoCodes = props.currentSearchManager.currentSearch.promoCodes;
	const canAddAnotherPromoCode = currentPromoCodes.length < 2;

	const handleApply = () => {
		if (!validate()) return;

		setIsOpen(false);
		setPromoCode("");
		setShowPromoCodeFormatError(false);
		setShowPromoCodeVoucherError(false);
		props.currentSearchManager.addPromoCode(promoCode.trim().toLowerCase());
	};

	const applyButton = iconButton({
		icon: html`<i class="jsh-circle-chevron-right1 text-base"></i>`,
		isAction: false,
		isDisabled: promoCode.length === 0,
		label: i18next.t("Aplicar"),
		onClick: handleApply,
	});

	const showPromoCode = () =>
		!userInfo?.ChileCompra?.IsMember &&
		(!props.currentSearchManager.currentSearch.useAmericanMiles || !userInfo?.AmericanAirlines?.IsMember);

	const init = () => {
		if (
			isInitialized ||
			!userInfo?.RoleCode ||
			!props.dynamicSettings ||
			currentPromoCodes.length > 0 ||
			userInfo.ChileCompra?.IsMember
		) {
			return;
		}

		try {
			const promoCodes = getPromoCodes(userInfo, props.culture, props.dynamicSettings);

			props.currentSearchManager.setPromoCodes(promoCodes);
			setPredefinedPromoCodes(promoCodes);
			setIsInitialized(true);
		} catch (e) {
			throw new Error("Unable to parse user info cookie or set WA pc.");
		}
	};

	const validate = () => {
		setShowPromoCodeVoucherError(false);
		setShowPromoCodeFormatError(false);

		if (VOUCHER_REGEX.test(promoCode)) {
			setShowPromoCodeVoucherError(true);
			return false;
		}

		if (!PROMO_CODE_REGEX.test(promoCode)) {
			setShowPromoCodeFormatError(true);
			return false;
		}

		return true;
	};

	const displayedPromoCode = (code: string) =>
		predefinedPromoCodes.includes(code) ? getMaskedPromoCode(code) : code.toUpperCase();

	const getMaskedPromoCode = (code: string) => {
		if (!code) return "";

		const isPeruCompraUser = userInfo?.PeruCompra?.IsAdmin || userInfo?.PeruCompra?.IsMember;
		const lengthToMask = isPeruCompraUser ? code.length : code.length - 2;

		return `${[...Array(lengthToMask)].map(() => "*").join("")}${code.substring(lengthToMask).toUpperCase()}`;
	};

	const handleBlur = () => {
		window.eventBus.raiseEvent({
			name: JetSmartEvent.PromoCodeAdded,
			params: {
				id: "searchbox.promocode",
				event: "input",
				data: {
					codes: currentPromoCodes,
				},
			},
		});
	};

	const handleInput = (inputPromoCode: string) => {
		setShowPromoCodeVoucherError(false);
		setShowPromoCodeFormatError(false);
		setPromoCode(inputPromoCode);
	};

	useEffectWithCleanup(init, [
		userInfo?.RoleCode,
		props.culture,
		props.dynamicSettings,
		props.currentSearchManager.currentSearch.useAmericanMiles,
		currentPromoCodes.length,
		isInitialized,
	]);

	useEffectWithoutCleanup(() => {
		setPromoCode("");
		setShowPromoCodeFormatError(false);
		setShowPromoCodeVoucherError(false);
	}, [isOpen]);

	useEffectWithoutCleanup(() => {
		if (props.currentSearchManager.currentSearch.useAmericanMiles && userInfo?.AmericanAirlines?.IsMember) {
			props.currentSearchManager.setPromoCodes([]);
			setShowPromoCodeVoucherError(false);
			setShowPromoCodeFormatError(false);
		}
	}, [props.currentSearchManager.currentSearch.useAmericanMiles, userInfo?.AmericanAirlines?.IsMember, currentPromoCodes.length]);

	const appliedPromoCodesTemplate = () =>
		currentPromoCodes.map(
			(code) => html`
				<div class="flex items-center justify-center gap-2 rounded-lg bg-[#F0F3F7] px-3 py-2">
					<div class="text-sm font-bold text-[#1B365D]">${displayedPromoCode(code)}</div>
					<div
						class="cursor-pointer text-[10px] text-[#1B365D]"
						@click=${() => props.currentSearchManager.removePromoCode(code)}
					>
						<i class="jsh-nope"></i>
					</div>
				</div>
			`
		);

	const cancelButtonTemplate = () => html`
		<div class="mb-1 cursor-pointer text-sm text-n-light-gray underline" @click=${() => setIsOpen(false)}>
			<i class="jsh-nope text-[10px]"></i> ${i18next.t("Cancelar")}
		</div>
	`;

	const errorTemplate = () =>
		showPromoCodeFormatError || showPromoCodeVoucherError
			? html`
					<div class="absolute inset-x-0 top-full rounded-lg bg-white p-2 text-xs text-n-red shadow-md">
						${showPromoCodeFormatError
							? i18next.t("promoCodeFormatError")
							: i18next.t("promoCodeVoucherError")}
					</div>
				`
			: "";

	const inputTemplate = () => {
		const isDisabled = userInfo?.PeruCompra?.IsAdmin || userInfo?.PeruCompra?.IsMember;
		const isInvalid = showPromoCodeFormatError || showPromoCodeVoucherError;

		return html`
			<div class="relative">
				${cancelButtonTemplate()}
				<ac-input
					.autoComplete=${"off"}
					.isDisabled=${isDisabled}
					.isInvalid=${isInvalid}
					.isSmall=${true}
					.placeholder=${i18next.t("search-box-promo-code")}
					.value=${promoCode}
					.onInput=${handleInput}
					.onBlur=${handleBlur}
				></ac-input>
				${errorTemplate()}
			</div>
		`;
	};

	const promoCodeInputTemplate = () => html` ${inputTemplate()} ${applyButton.htmlTemplate()} `;

	const promoCodeInputOpenerTemplate = () => html`
		<div
			class="flex cursor-pointer items-center gap-1.5 text-n-blue hover:text-n-cyan"
			@click=${() => setIsOpen(true)}
		>
			<span class="pb-0.5 text-2xl">+</span>
			<span class="text-nowrap text-sm hidden md:block">${i18next.t("Añadir código promocional")}</span>
			<span class="text-nowrap text-sm md:hidden">${i18next.t("Código promocional")}</span>
		</div>
	`;

	const mobileTemplate = () => html`
		<div>
			<div
				class=${classNames("items-end justify-start gap-2", {
					"grid grid-cols-2": isOpen,
					"flex items-end": !isOpen,
				})}
			>
				${canAddAnotherPromoCode
					? isOpen
						? promoCodeInputTemplate()
						: promoCodeInputOpenerTemplate()
					: html``}
			</div>
			<div
				class=${classNames("flex items-end justify-start gap-2", {
					"mt-4": isOpen && canAddAnotherPromoCode,
				})}
			>
				${appliedPromoCodesTemplate()}
			</div>
		</div>
	`;

	const desktopTemplate = () => html`
		<div class="flex items-end justify-end gap-2">
			${appliedPromoCodesTemplate()}
			${canAddAnotherPromoCode ? (isOpen ? promoCodeInputTemplate() : promoCodeInputOpenerTemplate()) : html``}
		</div>
	`;

	const htmlTemplate = () => {
		if (!showPromoCode()) return html``;

		return windowSize.size === "XS" || windowSize.size === "SM" ? mobileTemplate() : desktopTemplate();
	};

	return { predefinedPromoCodes, htmlTemplate };
};
